import {isLoggedIn} from '@github-ui/client-env'
import {BlockUserFromOrgDialog} from '@github-ui/commenting/BlockUserFromOrgDialog'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {ReportContentDialog} from '@github-ui/commenting/ReportContentDialog'
import {UnblockUserFromOrgDialog} from '@github-ui/commenting/UnblockUserFromOrgDialog'
import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {
  BlockedIcon,
  KebabHorizontalIcon,
  LinkIcon,
  MarkdownIcon,
  PencilIcon,
  QuoteIcon,
  ReportIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useMemo, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import type {IssueBodyHeaderActions_comment$key} from './__generated__/IssueBodyHeaderActions_comment.graphql'

type IssueViewerIssue = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>
type IssueViewerAuthor = NonNullable<IssueViewerIssue['author']>

export type IssueBodyHeaderActionsFragment = Pick<IssueViewerAuthor, 'id' | 'login'>

export type IssueBodyHeaderActionsProps = {
  url?: string
  issueBodyRef?: React.RefObject<HTMLDivElement | null>
  onReplySelect?: (quotedText?: string) => void
  startIssueBodyEdit: () => void
  onEditHover?: () => void
  viewerCanUpdate: boolean
  viewerCanReport?: boolean
  viewerCanReportToMaintainer?: boolean
  viewerCanBlockFromOrg?: boolean
  viewerCanUnblockFromOrg?: boolean
  issueId?: string
  owner?: string
  ownerId?: string
  ownerUrl?: string
  pendingBlock?: boolean
  pendingUnblock?: boolean
  isDisabled?: boolean
  // Optional name of the portal container to render the action menu overlay into.
  actionMenuPortalContainerName?: string
}

type IssueBodyHeaderActionsInternalProps = IssueBodyHeaderActionsProps & {
  author: IssueBodyHeaderActionsFragment
  comment: IssueBodyHeaderActions_comment$key
}

export function IssueBodyHeaderActions({
  url,
  issueBodyRef,
  onReplySelect,
  startIssueBodyEdit,
  onEditHover,
  viewerCanUpdate,
  viewerCanReport,
  viewerCanReportToMaintainer,
  viewerCanBlockFromOrg,
  viewerCanUnblockFromOrg,
  issueId,
  owner,
  ownerId,
  ownerUrl,
  author: actorData,
  pendingBlock,
  pendingUnblock,
  comment,
  isDisabled = false,
  actionMenuPortalContainerName,
}: IssueBodyHeaderActionsInternalProps) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [selection, setSelection] = useState<SelectionContext | null>(null)
  const [isBlockUserFromOrgOpen, setIsBlockUserFromOrgOpen] = useState(false)
  const [isUnblockUserFromOrgOpen, setIsUnblockUserFromOrgOpen] = useState(false)

  const commentData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyHeaderActions_comment on Comment {
        body
      }
    `,
    comment,
  )

  const reportUrl = useMemo(() => {
    if (!actorData || !url) return undefined
    const host = ssrSafeWindow?.location.origin
    if (!host) return undefined

    const query = `content_url=${encodeURIComponent(url)}&report=${actorData.login}+(user)`
    const reportTargetUrl = `${host}/contact/report-content?${query}`

    return reportTargetUrl
  }, [actorData, url])

  const copyToClipboard = (textToCopy: string) => {
    copyText(textToCopy)
  }

  const [isReportContentDialogOpen, setIsReportContentDialogOpen] = useState(false)

  const showReportSection = viewerCanReport || viewerCanReportToMaintainer

  const userCanBlock = canBlock(!!viewerCanBlockFromOrg, !!pendingBlock, !!pendingUnblock)
  const userCanUnblock = canUnblock(!!viewerCanUnblockFromOrg, !!pendingBlock, !!pendingUnblock)

  return (
    <>
      {isReportContentDialogOpen && owner && ownerUrl && issueId && (
        <ReportContentDialog
          owner={owner}
          ownerUrl={ownerUrl}
          reportUrl={reportUrl}
          contentId={issueId}
          onClose={() => setIsReportContentDialogOpen(false)}
          contentType={'issue'}
        />
      )}
      {isBlockUserFromOrgOpen && owner && ownerId && url && issueId && (
        <BlockUserFromOrgDialog
          onClose={() => setIsBlockUserFromOrgOpen(false)}
          organization={{login: owner, id: ownerId}}
          contentId={issueId}
          contentAuthor={{login: actorData.login, id: actorData.id}}
          contentUrl={url}
        />
      )}
      {isUnblockUserFromOrgOpen && owner && ownerId && url && issueId && (
        <UnblockUserFromOrgDialog
          onClose={() => setIsUnblockUserFromOrgOpen(false)}
          organization={{login: owner, id: ownerId}}
          contentAuthor={{login: actorData.login, id: actorData.id}}
          contentId={issueId}
        />
      )}
      <ActionMenu
        open={menuOpened}
        onOpenChange={(value: boolean) => {
          if (value) {
            const current = window.getSelection()
            if (current && current.anchorNode) {
              setSelection({
                anchorNode: current.anchorNode,
                range: current.getRangeAt(0),
              })
            } else {
              setSelection(null)
            }
          }
          setMenuOpened(value)
        }}
      >
        <ActionMenu.Anchor>
          <IconButton
            disabled={isDisabled}
            size="small"
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={LABELS.issueBodyHeaderActions}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay portalContainerName={actionMenuPortalContainerName}>
          <ActionList>
            <ActionList.Item
              inactiveText={!url || url === '' ? 'URL must be available to be copied' : undefined}
              onSelect={() => copyToClipboard(url || '')}
            >
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            {isFeatureEnabled('comment_viewer_copy_raw_markdown') ? (
              <ActionList.Item onSelect={() => copyToClipboard(commentData.body)}>
                <ActionList.LeadingVisual>
                  <MarkdownIcon />
                  {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
                </ActionList.LeadingVisual>
                Copy Markdown
              </ActionList.Item>
            ) : null}
            {isLoggedIn() && (
              <ActionList.Item
                onSelect={() => {
                  const newValue = selectQuoteFromComment(issueBodyRef?.current, selection, commentData.body)
                  onReplySelect?.(newValue)
                }}
              >
                <ActionList.LeadingVisual>
                  <QuoteIcon />
                </ActionList.LeadingVisual>
                Quote reply
              </ActionList.Item>
            )}
            {viewerCanUpdate && (
              <>
                <ActionList.Divider />
                <ActionList.Item
                  onSelect={() => {
                    startIssueBodyEdit()
                  }}
                  onMouseEnter={onEditHover}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit
                </ActionList.Item>
              </>
            )}
            {userCanBlock && (
              <>
                <ActionList.Divider />
                <ActionList.Item
                  onSelect={() => {
                    setIsBlockUserFromOrgOpen(true)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <BlockedIcon />
                  </ActionList.LeadingVisual>
                  Block user
                </ActionList.Item>
              </>
            )}
            {userCanUnblock && (
              <>
                <ActionList.Divider />
                <ActionList.Item
                  onSelect={() => {
                    setIsUnblockUserFromOrgOpen(true)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <BlockedIcon />
                  </ActionList.LeadingVisual>
                  Unblock user
                </ActionList.Item>
              </>
            )}
            {showReportSection && (
              <>
                <ActionList.Divider />
                {viewerCanReportToMaintainer ? (
                  <ActionList.Item
                    onSelect={() => {
                      setIsReportContentDialogOpen(true)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.Item>
                ) : (
                  <ActionList.LinkItem href={reportUrl} target="_blank">
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.LinkItem>
                )}
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

function canBlock(viewerCanBlockFromOrg: boolean, pendingBlock: boolean, pendingUnblock: boolean) {
  if (pendingBlock) {
    // The user has just blocked from this issue
    return false
  }
  if (pendingUnblock) {
    // The user has just unblocked from this issue
    return true
  }
  return viewerCanBlockFromOrg
}

function canUnblock(viewerCanUnblockFromOrg: boolean, pendingBlock: boolean, pendingUnblock: boolean) {
  if (pendingUnblock) {
    // The user has just unblocked from this issue
    return false
  }
  if (pendingBlock) {
    // The user has just blocked from this issue
    return true
  }
  return viewerCanUnblockFromOrg
}
